import { axiosB2 } from './axiosB2';
import { getB2Result, parseB2Envelope } from './b2Envelope';

/** @typedef {'account' | 'contact'} MarketingStatusEntityType */

/**
 * @typedef {object} MarketingStatusHistoryEntry
 * @property {number} Id
 * @property {number} MarketingStatusId
 * @property {string} MarketingStatus
 * @property {string} ChangeDate UTC ISO timestamp. May omit the `Z` suffix.
 * @property {number} ChangedByType Actor type. Known values: `1` = user (`label_marketing_status_changed_by_user`).
 * @property {string | { Key: string, Values?: Array<string|number>, Refs?: Array<{ Id: number, Entity: string }> }} ChangedByLabel i18n key or parameterized label when ChangedByUserName is absent.
 * @property {number|null} ChangedByUserId
 * @property {string|null} ChangedByUserName Display name when ChangedByType is user.
 * @property {number} Source Change origin. Known values: `1` = manual (`label_marketing_status_source_manual`).
 * @property {string | { Key: string, Values?: Array<string|number>, Refs?: Array<{ Id: number, Entity: string }> }} SourceLabel i18n key or parameterized label for "Source"
 * @property {string|null} Info Optional context shown in the history timeline.
 */

/**
 * @param {{ entityType: MarketingStatusEntityType, entityId: number }} params
 */
function assertMarketingStatusEntityParams({ entityType, entityId }) {
    if (entityType !== 'account' && entityType !== 'contact') {
        throw new Error('entityType must be "account" or "contact"');
    }
    const id = Number(entityId);
    if (!Number.isFinite(id) || id <= 0) {
        throw new Error('entityId must be a positive number');
    }
    return { entityType, entityId: id };
}

/**
 * Read-only count for "View history (n)" label. B2: GetMarketingStatusHistoryCount
 * @param {{ entityType: MarketingStatusEntityType, entityId: number }} params
 * @returns {Promise<number>}
 */
export function getMarketingStatusHistoryCount({ entityType, entityId }) {
    const params = assertMarketingStatusEntityParams({ entityType, entityId });
    return axiosB2.post('GetMarketingStatusHistoryCount', params).then(({ data }) => {
        const parsed = parseB2Envelope(data);
        const count = Number(getB2Result(parsed));
        if (!Number.isFinite(count) || count < 0) {
            throw new Error('Invalid marketing status history count');
        }
        return count;
    });
}

/**
 * Read-only history list for the history modal. B2: GetMarketingStatusHistory
 * @param {{ entityType: MarketingStatusEntityType, entityId: number }} params
 * @returns {Promise<MarketingStatusHistoryEntry[]>}
 */
export function getMarketingStatusHistory({ entityType, entityId }) {
    const params = assertMarketingStatusEntityParams({ entityType, entityId });
    return axiosB2.post('GetMarketingStatusHistory', params).then(({ data }) => {
        const parsed = parseB2Envelope(data);
        const result = getB2Result(parsed);
        return Array.isArray(result) ? result : [];
    });
}
